import { useEffect, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { Box, Button, ButtonBase, CircularProgress, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import { useAuth } from '@/features/auth/AuthContext';
import { adoptSession, getRefreshJwt } from '@/features/auth/descope';
import { stopViewAs } from '@/services/api';
import { Headset, ICON_STROKE, Minimize2 } from '@/components/ui/icons';
import { clearIdentityHints } from './sessionSlots';
import { radii } from '@/theme';

// The minimized strip: an orb parked at the right edge, at the strip's own height.
const ORB = 44;
const ORB_INSET = 16;
// From the strip's center (the viewport's center) to the orb's center, and back.
const TO_ORB = `calc(50vw - ${ORB_INSET + ORB / 2}px)`;
const TO_STRIP = `calc(${ORB_INSET + ORB / 2}px - 50vw)`;
// Both shapes ride the SAME curve in both directions, so they stay on top of each
// other the whole way and the cross-fade reads as one shape morphing. (Separate
// ease-in/ease-out per shape was tried: they drift apart mid-flight and it looks
// like two orange things.) Back-out overshoots a touch and settles — the
// "landing"; the leaving shape's overshoot happens after it has faded.
const TRIP_MS = 650;
const TRIP = `transform ${TRIP_MS}ms cubic-bezier(0.34, 1.56, 0.64, 1), opacity 250ms ease`;
// The trip is decoration; the banner being on screen is not. Reduced motion
// keeps both states and drops only the travel between them.
const calm = { '@media (prefers-reduced-motion: reduce)': { transition: 'none' } };

export interface ViewAsBannerProps {
  /** Injected by stories. The app reads the signed-in identity itself. */
  label?: string;
}

/**
 * "You are looking at someone else's account." Always on screen, never dismissable.
 *
 * The one rule every account-impersonation feature shares: it is never invisible.
 * Everything else about a view-as session is designed to be indistinguishable
 * from the customer's own — same data, same screens, same claims — so this strip
 * is the only thing standing between "checking a customer's view" and "not
 * noticing whose data you are reading".
 *
 * Fixed and above the modal layer on purpose: it has to survive every screen,
 * including a dialog opened over one.
 *
 * Minimizable, not dismissable: the strip covers the top-center of whatever is
 * under it, so it can roll itself up into an orange orb at the right edge — still
 * on screen, still orange, still naming the account on hover — and one click
 * unrolls it. Both shapes are always mounted and the state only flips their
 * transforms, so CSS transitions animate either direction (and an interrupted
 * trip reverses from wherever it is) with no exit-animation bookkeeping. The orb
 * is deliberately NOT remembered across a reload: a fresh page re-shows the full
 * strip, which is the safer default for a reminder of whose data this is.
 */
export function ViewAsBanner({ label }: ViewAsBannerProps) {
  const { user, signOut } = useAuth();
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  const [minimized, setMinimized] = useState(false);
  const orbRef = useRef<HTMLButtonElement>(null);
  const minimizeRef = useRef<HTMLButtonElement>(null);
  // While viewing as a customer, `user` IS the customer — that is the point.
  // `||`, not `??`: AuthContext already collapses a nameless profile to the
  // literal "User", and an injected label may be an empty string — either would
  // slip through a nullish chain and leave this strip naming nobody, which is
  // the one thing it exists to do.
  const who = label || user?.name || user?.email || 'this customer';

  // This banner is mounted for exactly as long as the session is a view-as one,
  // which makes it the right place to make sure the CUSTOMER's identity does not
  // outlive it. Clearing at the swap is not enough on its own: the SDK rewrites
  // these hints from the `me()` it fires on the next boot, so by now they hold
  // the customer — and the sign-in screen reads them for its "continue as…" card.
  // A clean exit heals itself (the admin's own me() overwrites); closing the tab
  // mid-session is the case this covers. `pagehide` rather than `beforeunload`:
  // it also fires when the tab is discarded or restored from the back/forward
  // cache, which `beforeunload` does not guarantee.
  useEffect(() => {
    const forget = () => clearIdentityHints(window.localStorage);
    window.addEventListener('pagehide', forget);
    return () => window.removeEventListener('pagehide', forget);
  }, []);

  const exit = async () => {
    setBusy(true);
    setFailed(false);
    try {
      adoptSession(await stopViewAs(getRefreshJwt()));
    } catch {
      // The trade-back failed — the view-as window expired, or the network went.
      // Sign out rather than strand someone inside a customer's account: it
      // costs one sign-in and it always works.
      //
      // `signOut` is fire-and-forget, so it can leave the app exactly as it is.
      // Re-enable the button and say so: a disabled Exit with no message is the
      // only way out of this session becoming no way out at all.
      setBusy(false);
      setFailed(true);
      signOut();
    }
  };

  // Focus follows the banner into its other shape — otherwise it is left on a
  // button that just went inert, and the keyboard is dropped back at <body>.
  // flushSync so the target is no longer inert by the time it is focused.
  const minimize = () => {
    flushSync(() => setMinimized(true));
    orbRef.current?.focus();
  };
  const restore = () => {
    flushSync(() => setMinimized(false));
    minimizeRef.current?.focus();
  };

  return (
    <>
      <Box
        inert={minimized}
        sx={{
          position: 'fixed',
          top: 12,
          left: '50%',
          // Matching transform lists on both sides, so the browser interpolates
          // them piece by piece instead of as a matrix. Squeezed on X only: a
          // ~450px strip at 0.1 is about the orb's size, so it collapses into a
          // ball rather than a line.
          transform: minimized
            ? `translateX(-50%) translateX(${TO_ORB}) scale(0.1, 1)`
            : 'translateX(-50%) translateX(0px) scale(1, 1)',
          opacity: minimized ? 0 : 1,
          transition: TRIP,
          ...calm,
          // Above dialogs: a modal must never be able to hide whose account this is.
          zIndex: (t) => t.zIndex.modal + 1,
          bgcolor: 'warning.main',
          color: 'warning.contrastText',
          // px STRING, per the note in theme/shape.ts: a bare number in `sx` is
          // multiplied by shape.borderRadius, so 999 would emit 7992.
          borderRadius: `${radii.pill}px`,
          boxShadow: 3,
          py: 0.75,
          pl: 2,
          pr: 0.75,
          maxWidth: 'calc(100vw - 32px)',
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Headset size={16} strokeWidth={ICON_STROKE} />
          <Typography
            variant="body2"
            sx={{ fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
          >
            {failed ? `Couldn't exit — signing out of ${who}` : `Support view: ${who} — read-only`}
          </Typography>
          <IconButton
            ref={minimizeRef}
            size="small"
            aria-label="Minimize support view banner"
            onClick={minimize}
            sx={{ color: 'inherit', flexShrink: 0 }}
          >
            <Minimize2 size={14} strokeWidth={ICON_STROKE} />
          </IconButton>
          <Button
            size="small"
            onClick={exit}
            disabled={busy}
            startIcon={busy ? <CircularProgress size={12} color="inherit" /> : undefined}
            sx={{
              bgcolor: 'warning.contrastText',
              color: 'warning.main',
              borderRadius: `${radii.pill}px`,
              px: 1.5,
              '&:hover': { bgcolor: 'warning.contrastText', opacity: 0.9 },
            }}
          >
            Exit
          </Button>
        </Stack>
      </Box>
      <Box
        inert={!minimized}
        sx={{
          position: 'fixed',
          top: 12,
          right: ORB_INSET,
          zIndex: (t) => t.zIndex.modal + 1,
          // It rolls: two full turns on the way out, unwound on the way back.
          transform: minimized
            ? 'translateX(0px) rotate(0deg) scale(1)'
            : `translateX(${TO_STRIP}) rotate(-720deg) scale(0.4)`,
          opacity: minimized ? 1 : 0,
          transition: TRIP,
          ...calm,
        }}
      >
        {/* Held until the orb has landed. The orb starts its trip right under the
            cursor (and under focus), so an ordinary 100ms delay opened the tooltip
            mid-flight — and a tooltip is positioned once, when it opens, so it
            stayed stranded halfway across the screen. */}
        <Tooltip title={`Support view: ${who} — click to expand`} placement="left" enterDelay={TRIP_MS}>
          <ButtonBase
            ref={orbRef}
            onClick={restore}
            sx={{
              width: ORB,
              height: ORB,
              borderRadius: '50%',
              bgcolor: 'warning.main',
              color: 'warning.contrastText',
              // Parked, it sits still — an idle bob was tried and read as noise.
              boxShadow: 6,
            }}
          >
            <Headset size={20} strokeWidth={ICON_STROKE} />
          </ButtonBase>
        </Tooltip>
      </Box>
    </>
  );
}
